import { classNames as cn } from "primereact/utils";
import React, { ReactNode, useMemo } from "react";
import { emptyStateImages, WSEmptyStateType } from "../../emptyStates";
import { WSOrientation } from "../../types/utils";
import { WSText } from "../core/WSText/WSText.component";
import { WSFlexBox } from "../layout";
import { WSActions, WSActionsProps } from "../WSActions/WSActions";
import {
  WSElement,
  WSMarginProps,
  WSPaddingProps
} from "../WSElement/WSElement.component";
import styles from "./WSEmptyState.module.scss";

export interface WSEmptyStateProps extends WSMarginProps, WSPaddingProps {
  type: WSEmptyStateType;
  title: string;
  description?: string | ReactNode;
  className?: string;
  actions?: WSActionsProps;
  orientation?: WSOrientation;
  size?: "S" | "M";
  hasBorder?: boolean;
  bgColor?: "transparent" | "white" | "gray";
  theme?: "neutral" | "garnet";
}

export const WSEmptyState: React.FC<WSEmptyStateProps> = ({
  type,
  orientation = "vertical",
  hasBorder,
  bgColor = "transparent",
  theme = "neutral",
  size = "M",
  title,
  description,
  actions,
  className,
  ...otherProps
}) => {
  const SVG = emptyStateImages[theme][type];

  const actionsProps = useMemo((): WSActionsProps | undefined => {
    if (!actions) return undefined;

    return {
      orientation,
      ...actions
    };
  }, [actions, orientation]);

  return (
    <WSElement
      px="M"
      py="L"
      className={cn(
        styles[`orientation-${orientation}`],
        styles[`bg-${bgColor}`],
        {
          [styles.border]: hasBorder
        },
        className
      )}
      {...otherProps}
    >
      <WSFlexBox.Center
        direction={orientation === "vertical" ? "column" : "row"}
      >
        <SVG className={styles[`img-${size}`]} />
        <WSFlexBox
          ml="XS"
          direction="column"
          className={cn(styles.textContent, styles[`size-${size}`])}
        >
          <WSText
            kind={size === "M" ? "Heading5" : "Paragraph"}
            className={styles.wordBreak}
            mb="XS"
            color="gray600"
            weight="medium"
          >
            {title}
          </WSText>
          {description && (
            <WSText.ParagraphSm
              className={styles.description}
              align={orientation === "vertical" ? "center" : "left"}
              mb={orientation === "vertical" ? "XL" : "M"}
              color="gray600"
              weight="book"
            >
              {description}
            </WSText.ParagraphSm>
          )}
          {!!actionsProps && <WSActions {...actionsProps} />}
        </WSFlexBox>
      </WSFlexBox.Center>
    </WSElement>
  );
};
